import { FunctionComponent, useEffect, useState } from 'react';
import {
  Box,
  Card,
  ELinxHistoryItem,
  withStyles,
  Icon,
  GetColorStatus,
  capitalizeFirstLetter,
  PaymentOption,
} from 'vopay';
import { Title, CopyButton, TransactionStatus, Button, Truncate } from 'UI';
import { useHistory, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useBranding } from '@/providers/Branding.provider';
import CardRows from '@/components/UI/CardRows';
import useFetchELinxHistory from '@/hooks/useApi/useFetchELinxHistory';
import styles from './ElinxConfirmationPage.module.scss';

const cn = withStyles(styles);

type ElinxType = 'pay' | 'collect' | 'connect' | 'flex';
type NotificationMethod = 'email' | 'sms' | 'none';

const ElinxConfirmationPage: FunctionComponent = () => {
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory' });
  const { logo } = useBranding();
  const { eLinxId, type, notificationMethod, eFlexOption } = useParams<{
    eLinxId: string;
    type: ElinxType;
    notificationMethod: NotificationMethod;
    eFlexOption: PaymentOption | string;
  }>();
  const [eLinx, setELinx] = useState<ELinxHistoryItem>();
  const [eLixResponse, , , fetchElinxHistory] = useFetchELinxHistory({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetchElinxHistory({ ELinxRequestID: eLinxId });

        if (!res.Success) {
          throw new Error(res.ErrorMessage);
        }
        setELinx(res.ELinxRequests[0]);
      } catch (error) {
        console.error(error);
        alertDialog.error(
          t('ErrorMessages.error', { defaultValue: 'Error' }),
          t('ErrorMessages.fetchingError', { defaultValue: 'An error occurred while fetching eLinx history' }),
        );
      }
    };

    fetchData();
  }, [eLinxId]);

  const eFlexOptionMap: Record<PaymentOption, string> = {
    multiple: t('Confirmation.multiple', { defaultValue: 'Multiple Payment Methods' }),
    partial: t('Confirmation.partial', { defaultValue: 'Partial Payment' }),
    installments: t('Confirmation.installments', { defaultValue: 'Installment Plan' }),
  };

  const formattedEflexOptions = eFlexOption
    .split(',')
    .filter((opt) => opt === 'multiple' || opt === 'partial' || opt === 'installments')
    .map((opt) => eFlexOptionMap[opt])
    .join(', ');

  const notificationMethodMap: Record<NotificationMethod, string> = {
    sms: t('Confirmation.sms', { defaultValue: 'SMS' }),
    email: t('Confirmation.email', { defaultValue: 'Email' }),
    none: '',
  };

  const elinxTypeMap: Record<ElinxType, string> = {
    pay: t('Confirmation.ePay', { defaultValue: 'ePay' }),
    collect: t('Confirmation.eCollect', { defaultValue: 'eCollect' }),
    connect: t('Confirmation.eConnect', { defaultValue: 'eConnect' }),
    flex: t('Confirmation.eFlex', { defaultValue: 'eFlex' }),
  };

  return (
    <Box ai="center" jc="center">
      {eLixResponse.Success && (
        <Card className={cn('card')}>
          <Box flx="col" gapSize="24px" padding="64px 32px 0">
            <Box flx="col" gapSize="12px" ai="center">
              <Icon icon="checkGreen" width="50px" height="50px" color="none" />
              <Title level="3">{t('Confirmation.title', { type: elinxTypeMap[type] })}</Title>
            </Box>

            {type === 'connect' && (
              <div className={cn('message', 'grey')}>
                <Trans
                  ns="translations"
                  i18nKey="ElinxHistory.Confirmation.connectSummary"
                  values={{ receiverName: eLinx?.ReceiverName }}
                  components={{
                    span: <span className={cn('bold')} />,
                  }}
                />
              </div>
            )}

            {type !== 'connect' && (
              <Box flx="col">
                <Trans
                  i18nKey="ElinxHistory.Confirmation.summary"
                  values={{
                    action: type === 'pay' ? 'Sent' : 'Requested',
                    currency: eLinx?.Currency,
                    amount: eLinx?.Amount,
                    direction: type === 'pay' ? 'to' : 'from',
                    name: eLinx?.ReceiverName,
                  }}
                  components={{
                    Text: <Title level="6" className={cn('message', 'grey')} />,
                    Amount: <Title level="2" className={cn('amount')} />,
                    Name: <span className={cn('bold')} />,
                  }}
                />
              </Box>
            )}

            <CardRows
              rows={[
                {
                  key: 1,
                  left: (
                    <div className={cn('rowLeft')}>
                      {t('Confirmation.embeddedLink', { defaultValue: 'eLinx Embedded Link' })}
                    </div>
                  ),
                  right: (
                    <Box gapSize="8px" ai="center">
                      <span className={cn('linkContainer', 'grey')}>
                        <Truncate text={eLinx?.Link || ''} maxLength={30} truncateEnding="..." />
                      </span>
                      <CopyButton content={eLinx?.Link || ''} />
                    </Box>
                  ),
                },
                {
                  key: 2,
                  left: <div className={cn('rowLeft')}>{t('Confirmation.elinxId', { defaultValue: 'eLinx ID' })}</div>,
                  right: <div className={cn('grey')}>{eLinx?.ELinxRequestID}</div>,
                },
                {
                  key: 3,
                  left: <div className={cn('rowLeft')}>{t('Confirmation.status', { defaultValue: 'Status' })}</div>,
                  right: (
                    <Box jc="fe">
                      <TransactionStatus
                        status={GetColorStatus(eLinx?.Status)}
                        label={capitalizeFirstLetter(eLinx?.Status || '')}
                      />
                    </Box>
                  ),
                },
                {
                  key: 4,
                  left: (
                    <div className={cn('rowLeft')}>
                      {t('Confirmation.notification', { defaultValue: 'Send Notifications via' })}
                    </div>
                  ),
                  right: (
                    <div className={cn('grey')}>
                      <span>{notificationMethodMap[notificationMethod]}</span>
                    </div>
                  ),
                  visible: notificationMethod !== 'none',
                },
                {
                  key: 5,
                  left: (
                    <div className={cn('rowLeft')}>
                      {t('Confirmation.eflexOptions', { defaultValue: 'eFlex Options' })}
                    </div>
                  ),
                  right: <div className={cn('grey')}>{formattedEflexOptions}</div>,
                  visible: eFlexOption !== 'none',
                },
              ]}
            />

            <div className={cn('buttonContainer')}>
              <Button
                size="small"
                onClick={() => {
                  history.push(`/elinx-history/details/${eLinx?.ELinxRequestID || ''}`);
                }}
              >
                {t('Confirmation.viewDetails', { type: elinxTypeMap[type] })}
              </Button>
              <Button
                size="small"
                colorScheme="primary"
                onClick={() => {
                  history.push(`/elinx/${type}`);
                }}
              >
                {t('Confirmation.createNew', { type: elinxTypeMap[type] })}
              </Button>
            </div>
          </Box>
          <div className={cn('copyright')}>
            <span>{t('Confirmation.poweredBy', { defaultValue: 'Powered by' })}</span>
            <img src={logo} alt="Logo" className={cn('logo')} />
          </div>
        </Card>
      )}
    </Box>
  );
};

export default ElinxConfirmationPage;
